// apps-kb-context-menu.js - Context menu for keyboard navigation

import state from './apps-state.js';
import * as folder from './apps-folders.js';
import * as iconops from './apps-icons.js';
import { keyboardState } from './apps-kb-state.js';
import * as helpers from './apps-kb-helpers.js';

export function showContextMenu(mouseX = null, mouseY = null) {
    console.log('showContextMenu called', mouseX, mouseY);
    hideContextMenu();
    
    const layout = helpers.getLayout();
    const isFolder = !!state.openFolder;
    const icons = helpers.getCurrentIcons();
    
    console.log('Cursor position:', keyboardState.virtualCursor.rank, keyboardState.virtualCursor.file);
    
    const icon = helpers.getIconAtRankFile(
        keyboardState.virtualCursor.rank,
        keyboardState.virtualCursor.file,
        icons,
        layout,
        isFolder
    );
    
    console.log('Icon found:', icon);
    if (!icon) {
        console.log('No icon at cursor position, returning');
        return;
    }
    
    const menuWidth = 180;
    const menuHeight = keyboardState.contextMenu.options.length * 42 + 16;
    const offset = 10;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let menuLeft, menuTop;
    
    // If mouse position provided (right-click), use it
    if (mouseX !== null && mouseY !== null) {
        menuLeft = mouseX;
        menuTop = mouseY;
        
        // Adjust if too close to right edge
        if (menuLeft + menuWidth > viewportWidth) {
            menuLeft = mouseX - menuWidth;
        }
        
        // Adjust if too close to bottom edge
        if (menuTop + menuHeight > viewportHeight) {
            menuTop = viewportHeight - menuHeight - offset;
        }
        
        // Ensure not off left edge
        if (menuLeft < 0) {
            menuLeft = offset;
        }
        
        // Ensure not off top edge
        if (menuTop < 0) {
            menuTop = offset;
        }
    } else {
        // Original keyboard positioning logic
        const currentScreen = helpers.getCurrentScreen();
        const screenRegions = isFolder ? layout.leaves : layout.leaves * layout.bands;
        const slotInfo = helpers.rankFileToRegionPosition(keyboardState.virtualCursor.rank, keyboardState.virtualCursor.file, layout, isFolder);
        console.log('Slot info:', slotInfo);
        if (!slotInfo) {
            console.log('No slot info, returning');
            return;
        }
        const screenAdjustedRegion = currentScreen * screenRegions + slotInfo.region;
        
        // Use the correct container for folder vs main screen
        const container = isFolder ? 
            document.getElementById('folderScreens') : 
            document.getElementById('appspaceScreens');
        
        console.log('Container:', container);
        if (!container) {
            console.log('No container found, returning');
            return;
        }
        
        const slotElement = container.querySelector(
            `.icon-slot[data-region="${screenAdjustedRegion}"][data-position="${slotInfo.position}"]`
        );
        console.log('Slot element:', slotElement);
        if (!slotElement) {
            console.log('No slot element found, returning');
            return;
        }
        
        const iconElement = slotElement.querySelector('.appicon');
        const rect = iconElement ? iconElement.getBoundingClientRect() : slotElement.getBoundingClientRect();
        
        console.log('Creating menu at rect:', rect);
        
        menuLeft = rect.left + rect.width + offset;
        menuTop = rect.top;
        
        if (menuLeft + menuWidth > viewportWidth) {
            menuLeft = rect.left - menuWidth - offset;
        }
        
        if (menuLeft < 0) {
            menuLeft = offset;
        }
        
        if (menuTop + menuHeight > viewportHeight) {
            menuTop = viewportHeight - menuHeight - offset;
        }
        
        if (menuTop < 0) {
            menuTop = offset;
        }
    }
    
    const menu = document.createElement('div');
    menu.id = 'keyboardContextMenu';
    menu.style.cssText = `
        position: fixed;
        left: ${menuLeft}px;
        top: ${menuTop}px;
        background: white;
        border: 2px solid #667eea;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        padding: 8px;
        z-index: 10000;
        min-width: ${menuWidth}px;
    `;
    
    keyboardState.contextMenu.options.forEach((option, index) => {
        const item = document.createElement('div');
        item.className = 'context-menu-item';
        item.dataset.index = index;
        item.textContent = `${option.label} (${option.key})`;
        item.style.cssText = `
            padding: 10px 15px;
            cursor: pointer;
            border-radius: 4px;
            font-size: 14px;
            display: flex;
            justify-content: space-between;
            align-items: center;
        `;
        
        if (index === keyboardState.contextMenu.selectedIndex) {
            item.style.background = '#667eea';
            item.style.color = 'white';
        }
        
        // Track mousedown to ensure full click cycle
        let mousedownOnItem = false;
        
        // Mouse hover
        item.addEventListener('mouseenter', () => {
            keyboardState.contextMenu.selectedIndex = index;
            updateMenuHighlight();
        });
        
        // Track mousedown on item
        item.addEventListener('mousedown', (e) => {
            // Only track left clicks
            if (e.button === 0) {
                mousedownOnItem = true;
            }
        });
        
        // Clear tracking on mouse leave
        item.addEventListener('mouseleave', () => {
            mousedownOnItem = false;
        });
        
        // Touch hover
        item.addEventListener('touchstart', (e) => {
            e.preventDefault();
            keyboardState.contextMenu.selectedIndex = index;
            updateMenuHighlight();
        });
        
        // Click/tap to activate - only if mousedown also happened on this item
        item.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // For mouse events, verify the full click cycle happened on this item
            if (e.pointerType === 'mouse' || e.type === 'click') {
                if (!mousedownOnItem) {
                    return; // Ignore clicks that didn't start on this item
                }
            }
            
            activateContextMenuItem(() => {
                import('./apps-kb-move-mode.js').then(moveMode => {
                    moveMode.enterMoveMode();
                });
            });
        });
        
        menu.appendChild(item);
    });
    
    document.body.appendChild(menu);
    keyboardState.contextMenu.active = true;
}

function updateMenuHighlight() {
    const menu = document.getElementById('keyboardContextMenu');
    if (!menu) return;
    
    const items = menu.querySelectorAll('.context-menu-item');
    items.forEach((item, index) => {
        if (index === keyboardState.contextMenu.selectedIndex) {
            item.style.background = '#667eea';
            item.style.color = 'white';
        } else {
            item.style.background = '';
            item.style.color = '';
        }
    });
}

export function hideContextMenu() {
    const menu = document.getElementById('keyboardContextMenu');
    if (menu) {
        menu.remove();
    }
    keyboardState.contextMenu.active = false;
    keyboardState.contextMenu.selectedIndex = 0;
}

export function navigateContextMenu(direction) {
    if (!keyboardState.contextMenu.active) return;
    
    const options = keyboardState.contextMenu.options;
    
    if (direction === 'up') {
        keyboardState.contextMenu.selectedIndex--;
        if (keyboardState.contextMenu.selectedIndex < 0) {
            keyboardState.contextMenu.selectedIndex = options.length - 1;
        }
    } else if (direction === 'down') {
        keyboardState.contextMenu.selectedIndex++;
        if (keyboardState.contextMenu.selectedIndex >= options.length) {
            keyboardState.contextMenu.selectedIndex = 0;
        }
    }
    
    updateMenuHighlight();
}

export function activateContextMenuItem(enterMoveMode) {
    if (!keyboardState.contextMenu.active) return;
    
    const option = keyboardState.contextMenu.options[keyboardState.contextMenu.selectedIndex];
    hideContextMenu();
    
    executeIconAction(option.action, enterMoveMode);
}

export function performEditAction() {
    // Directly perform edit action (longpress behavior) without opening menu
    const layout = helpers.getLayout();
    const isFolder = !!state.openFolder;
    const icons = helpers.getCurrentIcons();
    
    const icon = helpers.getIconAtRankFile(
        keyboardState.virtualCursor.rank,
        keyboardState.virtualCursor.file,
        icons,
        layout,
        isFolder
    );
    
    if (!icon) return;
    
    if (icon.type === 'app') {
        iconops.openEditModal(icon);
    } else if (icon.type === 'folder') {
        folder.openFolderView(icon);
    }
}

export function performOpenAction() {
    // Directly perform open action (launch app or open folder)
    const layout = helpers.getLayout();
    const isFolder = !!state.openFolder;
    const icons = helpers.getCurrentIcons();
    
    const icon = helpers.getIconAtRankFile(
        keyboardState.virtualCursor.rank,
        keyboardState.virtualCursor.file,
        icons,
        layout,
        isFolder
    );
    
    if (!icon) return;
    
    if (icon.type === 'folder') {
        folder.openFolderView(icon);
    } else if (icon.url) {
        window.location.href = icon.url;
    }
}

function executeIconAction(action, enterMoveMode) {
    const layout = helpers.getLayout();
    const isFolder = !!state.openFolder;
    const icons = helpers.getCurrentIcons();
    
    const icon = helpers.getIconAtRankFile(
        keyboardState.virtualCursor.rank,
        keyboardState.virtualCursor.file,
        icons,
        layout,
        isFolder
    );
    
    if (!icon) return;
    
    switch (action) {
        case 'open':
            if (icon.type === 'folder') {
                folder.openFolderView(icon);
            } else if (icon.url) {
                window.location.href = icon.url;
            }
            break;
        case 'edit':
            if (icon.type === 'app') {
                iconops.openEditModal(icon);
            } else if (icon.type === 'folder') {
                folder.openFolderView(icon);
            }
            break;
        case 'move':
            enterMoveMode();
            break;
    }
}

export function showGlobalContextMenu(mouseX, mouseY) {
    console.log('showGlobalContextMenu called at', mouseX, mouseY);
    hideContextMenu();
    hideGlobalContextMenu();
    
    console.log('Creating global context menu...');
    
    const menuWidth = 200;
    const menuHeight = 2 * 42 + 16; // 2 items
    const offset = 10;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let menuLeft = mouseX;
    let menuTop = mouseY;
    
    // Adjust if too close to right edge
    if (menuLeft + menuWidth > viewportWidth) {
        menuLeft = mouseX - menuWidth;
    }
    
    // Adjust if too close to bottom edge
    if (menuTop + menuHeight > viewportHeight) {
        menuTop = viewportHeight - menuHeight - offset;
    }
    
    // Ensure not off left edge
    if (menuLeft < 0) {
        menuLeft = offset;
    }
    
    // Ensure not off top edge
    if (menuTop < 0) {
        menuTop = offset;
    }
    
    console.log('Menu position calculated:', menuLeft, menuTop);
    
    const menu = document.createElement('div');
    menu.id = 'globalContextMenu';
    menu.style.cssText = `
        position: fixed;
        left: ${menuLeft}px;
        top: ${menuTop}px;
        background: white;
        border: 2px solid #667eea;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        padding: 8px;
        z-index: 10000;
        min-width: ${menuWidth}px;
    `;
    
    console.log('Menu element created');
    
    const globalOptions = [
        { label: 'Install from Catalog...', url: '/catalog' },
        { label: 'About', url: '/about/' }
    ];
    
    globalOptions.forEach((option, index) => {
        console.log('Creating menu item:', option.label);
        const item = document.createElement('div');
        item.className = 'global-context-menu-item';
        item.dataset.index = index;
        item.textContent = option.label;
        item.style.cssText = `
            padding: 10px 15px;
            cursor: pointer;
            border-radius: 4px;
            font-size: 14px;
        `;
        
        // Track mousedown to ensure full click cycle
        let mousedownOnItem = false;
        
        // Mouse hover
        item.addEventListener('mouseenter', () => {
            // Highlight on hover
            const items = menu.querySelectorAll('.global-context-menu-item');
            items.forEach(i => {
                i.style.background = '';
                i.style.color = '';
            });
            item.style.background = '#667eea';
            item.style.color = 'white';
        });
        
        // Mouse leave
        item.addEventListener('mouseleave', () => {
            item.style.background = '';
            item.style.color = '';
            mousedownOnItem = false;
        });
        
        // Track mousedown on item
        item.addEventListener('mousedown', (e) => {
            if (e.button === 0) {
                mousedownOnItem = true;
            }
        });
        
        // Touch hover
        item.addEventListener('touchstart', (e) => {
            e.preventDefault();
            const items = menu.querySelectorAll('.global-context-menu-item');
            items.forEach(i => {
                i.style.background = '';
                i.style.color = '';
            });
            item.style.background = '#667eea';
            item.style.color = 'white';
        });
        
        // Click/tap to activate
        item.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // For mouse events, verify the full click cycle happened on this item
            if (e.pointerType === 'mouse' || e.type === 'click') {
                if (!mousedownOnItem) {
                    return;
                }
            }
            
            hideGlobalContextMenu();
            window.location.href = option.url;
        });
        
        menu.appendChild(item);
    });
    
    console.log('About to append menu to body, menu has', menu.children.length, 'items');
    document.body.appendChild(menu);
    console.log('Menu appended to body, checking if it exists:', document.getElementById('globalContextMenu'));
}

export function hideGlobalContextMenu() {
    const menu = document.getElementById('globalContextMenu');
    if (menu) {
        menu.remove();
    }
}
